import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Linkedin } from "lucide-react";
import { NAV_LINKS, BUSINESS_EMAIL, SOCIAL_LINKS, REGISTERED_COMPANY } from "../lib/site";
import { Reveal } from "./Motion";
import { Logo } from "./Header";

const SOCIAL_ICONS = { Instagram, Linkedin };

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M16.5 2h-3.2v13.4c0 1.5-1.2 2.7-2.7 2.7a2.7 2.7 0 1 1 0-5.4c.3 0 .6 0 .9.1v-3.3a5.9 5.9 0 0 0-.9-.1A6 6 0 1 0 16.5 15V8.6c1.2.8 2.6 1.3 4.1 1.3V6.7a4.7 4.7 0 0 1-4.1-4.7Z" />
    </svg>
  );
}

const RESOURCE_LINKS = [
  { label: "Commercial Terms", to: "/commercial-terms" },
  { label: "Private Label", to: "/private-label" },
  { label: "Documentation", to: "/documentation" },
  { label: "FAQ", to: "/faq" },
  { label: "Privacy Policy", to: "/privacy-policy" },
];

const PRODUCT_LINKS = [
  { label: "Shisha Charcoal", to: "/products/coconut-shell-shisha-charcoal" },
  { label: "BBQ Charcoal", to: "/products/bbq-coconut-charcoal" },
];

const COMING_SOON = ["Biomass & Sawdust", "Coconut Products", "Indonesian Coffee", "Indonesian Spices", "Cocoa"];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-charcoal text-offwhite">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <div className="py-20 sm:py-28 border-b border-white/10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-clay mb-6">Start a conversation</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95]">
              Let's talk about
              <br />
              your supply.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/request-a-quote"
              data-testid="footer-quote-cta"
              className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="py-14 grid grid-cols-2 md:grid-cols-4 gap-10 border-b border-white/10">
          <div className="col-span-2">
            <Logo />
            <p className="mt-5 text-sm text-offwhite/50 leading-relaxed max-w-sm">
              PT HAQILA GLOBAL TRADING — an Indonesian commodity sourcing and export partner connecting international
              buyers with selected Indonesian manufacturers and producers.
            </p>
            <div data-testid="footer-social-links" className="mt-6 flex items-center gap-3">
              {SOCIAL_LINKS.map((s) => {
                const Icon = SOCIAL_ICONS[s.icon] || TikTokIcon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                    className="w-9 h-9 flex items-center justify-center border border-white/15 text-offwhite/60 hover:text-offwhite hover:border-clay transition-colors duration-300"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
            <div data-testid="footer-registered-company" className="mt-8 pt-6 border-t border-white/10 max-w-sm">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-clay mb-3">Registered Company</p>
              <dl className="space-y-1.5">
                {REGISTERED_COMPANY.map((row) => (
                  <div key={row.label} className="flex items-baseline gap-2 text-xs text-offwhite/55">
                    <dt className="shrink-0">{row.label}:</dt>
                    <dd className="text-offwhite/70 break-words">
                      {row.label === "Email" ? (
                        <a href={`mailto:${row.value}`} className="hover:text-offwhite transition-colors duration-300">
                          {row.value}
                        </a>
                      ) : (
                        row.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-offwhite/40 mb-5">Navigate</p>
            <ul className="space-y-3">
              {[...NAV_LINKS, ...RESOURCE_LINKS].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.to === "/privacy-policy" ? "footer-privacy-policy-link" : undefined}
                    className="text-sm text-offwhite/60 hover:text-offwhite transition-colors duration-300"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-offwhite/40 mb-5">Products</p>
            <ul className="space-y-3">
              {PRODUCT_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-offwhite/60 hover:text-offwhite transition-colors duration-300"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              {COMING_SOON.map((name) => (
                <li key={name} className="text-sm text-offwhite/30">
                  {name} <span className="text-[10px] uppercase tracking-widest text-clay/70">soon</span>
                </li>
              ))}
              <li>
                <a
                  href={`mailto:${BUSINESS_EMAIL}`}
                  className="text-sm text-offwhite/60 hover:text-offwhite transition-colors duration-300 break-all"
                >
                  {BUSINESS_EMAIL}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-offwhite/35 tracking-wide">
            © {new Date().getFullYear()} PT HAQILA GLOBAL TRADING. All rights reserved.
          </p>
          <p className="text-xs text-offwhite/35 tracking-wide max-w-md">
            HAQILA is a sourcing and export partner. Production is carried out by selected independent Indonesian
            manufacturing partners.
          </p>
        </div>
      </div>
    </footer>
  );
}
